
import { GetRef, Table } from 'antd';
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppGlobStore } from 'src/appGlobalStore';
import { DomUtil } from 'src/util/DomUtil';
import type { XmColumnsType, XmTableEntry, XmTableProps } from "./tableTypes";


function VirtualTable<T extends XmTableEntry>(props: Readonly<XmTableProps<T & XmTableEntry>>) {
  const mainScrollRef = useAppGlobStore(state => state.mainScrollRef);
  const tableRef = useRef<GetRef<typeof Table>>(null);

  // Horizontal virtualization support
  const maxVisibleColumns = props.maxVisibleColumns ?? 30;
  const horizontalVirtualization = props.horizontalVirtualization === true;

  // Simplified horizontal virtualization - just limit columns if enabled
  const finalColumns = useMemo<XmColumnsType<T & XmTableEntry>>(() => {
    type ColumnType = XmColumnsType<T & XmTableEntry>[number];
    const columnsFromProps: ColumnType[] | undefined = props.columns ? Array.from(props.columns) : undefined;

    if (!horizontalVirtualization || !columnsFromProps) {
      return columnsFromProps;
    }

    // For now, just limit the number of columns to prevent overwhelming DOM
    // Later we can add proper scroll-based virtualization
    if (columnsFromProps.length <= maxVisibleColumns) {
      return columnsFromProps;
    }

    // Keep fixed columns (first column) + limited scrollable columns
    const partitionedColumns = columnsFromProps.reduce<{ fixed: ColumnType[]; scrollable: ColumnType[]; }>((acc, column) => {
      if ('fixed' in column && column.fixed) {
        acc.fixed.push(column);
        return acc;
      }
      acc.scrollable.push(column);
      return acc;
    }, { fixed: [], scrollable: [] });

    const availableScrollableSlots = Math.max(maxVisibleColumns - partitionedColumns.fixed.length, 0);
    const visibleScrollableColumns = partitionedColumns.scrollable.slice(0, availableScrollableSlots);

    return [...partitionedColumns.fixed, ...visibleScrollableColumns];
  }, [props.columns, horizontalVirtualization, maxVisibleColumns]);


  const updateMaxWidthOverflow = useCallback(() => {
    const root = tableRef.current?.nativeElement;
    if (!root) return;

    // First match of each only, so nested sub-tables (expandable rows) aren't affected.
    const setMaxWidth = (className: string, value: string) => {
      const found: HTMLElement[] = [];
      findElementsByClass(root, className, found);
      if (found[0]) found[0].style.maxWidth = value;
    };
    // rc-virtual renders NO .ant-table-body (rc-table only creates that wrapper when
    // components.body is not a render fn) — the body is .ant-table-tbody-virtual-holder.
    // So cap the header AND the holder, keeping .ant-table-body for a non-virtual body.
    const applyToViewports = (value: string) => {
      setMaxWidth("ant-table-header", value);
      setMaxWidth("ant-table-tbody-virtual-holder", value);
      setMaxWidth("ant-table-body", value);
    };

    // A stretch table spans its container on purpose. CLEAR rather than bail, or one that
    // turns stretch on later keeps the max-width written on an earlier pass.
    if (props.className?.includes('xm-table-stretch')) return applyToViewports('');

    // Cap the header + body to the width available at the table's OWN position, so a
    // too-wide virtual table scrolls INTERNALLY. closestScrollable resolves to the
    // outermost non-scrolling child of the scroll container — #main-scroll's page block
    // normally, the modal body inside a modal; mainScrollRef is only the fallback for a
    // table with no scrollable ancestor. Capping to it unconditionally gave a modal table
    // the whole page as its cap, so it overflowed.
    const container = DomUtil.closestScrollable(root) ?? mainScrollRef.current;
    const containerWidth = container?.clientWidth;
    if (!containerWidth) return;
    applyToViewports(availableWidth(root, container) + "px");
  }, [mainScrollRef, props.className]);

  const triggerInitialScroll = useCallback(() => {
    const tableBodyElement = tableRef.current?.nativeElement?.querySelector('.ant-table-body');
    if (!(tableBodyElement instanceof HTMLElement)) return;

    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

    if (isFirefox) {
      tableBodyElement.scrollTop = 0;
      requestAnimationFrame(() => {
        const scrollEvent = new Event('scroll', { bubbles: true, cancelable: false });
        tableBodyElement.dispatchEvent(scrollEvent);
        requestAnimationFrame(() => {
          tableBodyElement.scrollTop = 1;
          requestAnimationFrame(() => {
            tableBodyElement.scrollTop = 0;
            // Force virtualization refresh for Firefox
            // setTimeout(() => {
            //   setVtKey(prev => prev + 1);
            // }, 100);
          });
        });
      });
    } else {
      tableBodyElement.scrollTop = 0;
      setTimeout(() => {
        const scrollEvent = new Event('scroll', { bubbles: true });
        tableBodyElement.dispatchEvent(scrollEvent);
        tableBodyElement.scrollTop = 1;
        setTimeout(() => {
          tableBodyElement.scrollTop = 0;
        }, 100);
      }, 200);
    }
  }, []); //setVtKey


  // Re-cap the width whenever the table or its scroll container resizes. Both targets are
  // needed: the container width bounds the cap, the table's own width is what gets capped.
  useEffect(() => {
    const tableElement = tableRef.current?.nativeElement;
    if (!tableElement) return undefined;
    window.addEventListener('resize', updateMaxWidthOverflow);
    const scrollableElement = DomUtil.closestScrollable(tableElement) ?? tableElement.parentElement;
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => updateMaxWidthOverflow())
      : null;
    if (resizeObserver) {
      if (scrollableElement) resizeObserver.observe(scrollableElement);
      resizeObserver.observe(tableElement);
    }
    updateMaxWidthOverflow();
    return () => {
      window.removeEventListener('resize', updateMaxWidthOverflow);
      resizeObserver?.disconnect();
    };
  }, [updateMaxWidthOverflow]);

  useEffect(() => {
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const delay = isFirefox ? 500 : 300;
    const timeoutId = setTimeout(() => {
      triggerInitialScroll();
    }, delay);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [triggerInitialScroll]);

  return (
    <Table<T>
      bordered={true}
      {...props}
      columns={finalColumns as ColumnsType<T>}
      pagination={false}
      scroll={props.noScrollY ? undefined : props.scroll}
      virtual
      //components={props.noScrollY ? props.components : vt}
      ref={tableRef}
    />
  );
};

/**
 * Content width available to `root` inside `container`: the container's inner width minus
 * every horizontal inset (margin / border / padding) of the wrappers between the two.
 * Replaces a blind `containerWidth - 20` slack, which showed as a ~20px empty strip right
 * of every hugging table (`.xm-table-with-toolbar` is inline-block, so it shrinks to the
 * capped header/holder and the slack becomes a visible gap) and, where the container IS
 * the table root, fed a shrink-per-resize loop.
 *
 * Insets are measured rather than the table's own left offset on purpose: an offset-based
 * measure depends on the table's current width and oscillates for centred tables.
 */
function availableWidth(root: HTMLElement, container: HTMLElement): number {
  let available = container.clientWidth;
  for (let el: HTMLElement | null = root; el && el !== container; el = el.parentElement) {
    const style = getComputedStyle(el);
    available -= (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
    if (el === root) continue;
    available -= (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
      + (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
  }
  return Math.max(Math.floor(available), 0);
}

function findElementsByClass(node: HTMLElement | null, className: string, foundElements: HTMLElement[]) {
  if (node?.classList?.contains(className))
    foundElements.push(node);

  if (node?.childNodes) {
    for (const childNode of node.childNodes)
      findElementsByClass(childNode as HTMLElement, className, foundElements);
  }
}

export default VirtualTable;